"use client";

import { useEffect, useState } from "react";
import { TrackJS } from "trackjs-nextjs";
import { isProductionBuild } from "@/lib/config/env";
import { trackErrorEvent } from "@/lib/utils/ga4-events";

export default function GlobalError({ error, reset }: { error: Error & { digest?: string }; reset: () => void }) {
  const [errorId] = useState(() => Date.now().toString());
  const [isRetrying, setIsRetrying] = useState(false);

  useEffect(() => {
    // Enhanced error tracking
    try {
      const enhancedError = {
        name: error.name,
        message: error.message,
        stack: error.stack,
        digest: error.digest,
        timestamp: new Date().toISOString(),
        url: typeof window !== "undefined" ? window.location.href : "unknown",
        userAgent: typeof window !== "undefined" ? window.navigator.userAgent : "unknown",
        errorBoundary: "GlobalError",
        level: "critical"
      };

      // Track with TrackJS
      TrackJS.track(error);

      // Add metadata individually to avoid type issues
      Object.entries(enhancedError).forEach(([key, value]) => {
        if (value !== undefined && value !== null) {
          TrackJS.addMetadata(key, String(value));
        }
      });

      trackErrorEvent(error);

      // Console logging for development
      if (!isProductionBuild()) {
        console.group("💀 Global Error Caught");
        console.error("Error:", error);
        console.error("Enhanced Context:", enhancedError);
        console.groupEnd();
      }
    } catch (trackingError) {
      console.error("Failed to track global error:", trackingError);
      console.error("Original error:", error);
    }
  }, [error]);

  const handleRetry = async () => {
    setIsRetrying(true);

    try {
      TrackJS.track(new Error("Global error retry attempted"));
    } catch {
      console.log("Global error retry attempted");
    }

    // Add a small delay to show loading state
    await new Promise((resolve) => setTimeout(resolve, 500));

    reset();
  };

  const handleReload = () => {
    try {
      TrackJS.track(new Error("Global error page reload triggered"));
    } catch {
      console.log("Global error page reload triggered");
    }

    if (typeof window !== "undefined") {
      window.location.href = "/";
    }
  };

  return (
    <html lang="en">
      <head>
        <title>Something went wrong</title>
        <meta name="viewport" content="width=device-width, initial-scale=1, maximum-scale=5, user-scalable=yes" />
        {/* Preload critical fonts with font-display optimization */}
        <link rel="preload" href="https://use.typekit.net/ciy2jho.css" as="style" crossOrigin="anonymous" />
        <link rel="stylesheet" href="https://use.typekit.net/ciy2jho.css" crossOrigin="anonymous" />

        {/* Fallback for JavaScript disabled */}
        <noscript>
          <link rel="stylesheet" href="https://use.typekit.net/ciy2jho.css" crossOrigin="anonymous" />
        </noscript>

        {/* Font display optimization - Force font-display: swap for all TypeKit fonts */}
        <style
          dangerouslySetInnerHTML={{
            __html: `
            @font-face {
              font-family: 'aktiv-grotesk-fallback';
              src: local('Arial'), local('Helvetica'), local('sans-serif');
              font-display: swap;
              ascent-override: 92%;
              descent-override: 22%;
              line-gap-override: 0%;
              size-adjust: 109%;
            }
            @font-face {
              font-family: 'proxima-nova-fallback';
              src: local('Arial'), local('Helvetica'), local('sans-serif');
              font-display: swap;
              ascent-override: 88%;
              descent-override: 21%;
              line-gap-override: 0%;
              size-adjust: 108%;
            }
            @font-face {
              font-family: 'aktiv-grotesk';
              font-display: swap;
            }
            @font-face {
              font-family: 'proxima-nova';
              font-display: swap;
            }
          `
          }}
        />
      </head>
      <body>
        <div className="error-boundary">
          <div className="error-boundary__content">
            <div className="error-boundary__container">
              <div className="error-boundary__icon-wrapper">
                <div className="error-icon">🧘‍♀️</div>
              </div>

              <h1 className="error-boundary__title">Something went wrong</h1>

              <div className="error-boundary__message">
                <p>We apologize for the inconvenience. An unexpected error has occurred and we're working to fix it.</p>
                <p>
                  You can try{" "}
                  <button
                    onClick={handleReload}
                    className="error-page__link"
                    style={{
                      background: "none",
                      border: "none",
                      color: "inherit",
                      textDecoration: "underline",
                      cursor: "pointer",
                      fontSize: "inherit",
                      fontFamily: "inherit"
                    }}
                  >
                    refreshing the page
                  </button>{" "}
                  or contact support if the problem persists.
                </p>
              </div>

              <div className="error-boundary__actions">
                <button
                  onClick={() => handleReload()}
                  className="btn btn--round btn__navigation btn__bg--light-grey"
                  type="button"
                >
                  <div className="btn__navigation__transition-container"></div>
                  <div className="btn__navigation__content-container">
                    <span className="btn__navigation__content">Go to Homepage</span>
                    <div className="svg-icon icon-type__feather btn__navigation__icon">
                      <svg
                        className="svg-icon__feather__svg"
                        width="24"
                        height="24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="1"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      >
                        <use xlinkHref="/assets/sprites/feather-sprite-v4.29.0.svg#arrow-right"></use>
                      </svg>
                    </div>
                  </div>
                </button>

                <button
                  onClick={() => (window.location.href = "/sessions")}
                  className="btn btn--round btn__navigation btn__bg--dark-grey"
                  type="button"
                >
                  <div className="btn__navigation__transition-container"></div>
                  <div className="btn__navigation__content-container">
                    <span className="btn__navigation__content">Browse Sessions</span>
                    <div className="svg-icon icon-type__feather btn__navigation__icon">
                      <svg
                        className="svg-icon__feather__svg"
                        width="24"
                        height="24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="1"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      >
                        <use xlinkHref="/assets/sprites/feather-sprite-v4.29.0.svg#arrow-right"></use>
                      </svg>
                    </div>
                  </div>
                </button>
              </div>

              <div className="error-boundary__help">
                <p className="error-boundary__help-text">
                  Need help?{" "}
                  <button
                    onClick={() => (window.location.href = "/contact")}
                    className="error-page__link"
                    style={{
                      background: "none",
                      border: "none",
                      color: "inherit",
                      textDecoration: "underline",
                      cursor: "pointer",
                      fontSize: "inherit",
                      fontFamily: "inherit"
                    }}
                  >
                    Contact our support team
                  </button>
                </p>
              </div>

              {!isProductionBuild() && error && (
                <details className="error-boundary__details">
                  <summary>Technical Details (Development Only)</summary>
                  <div className="error-boundary__error-content">
                    <strong>{error.name}:</strong> {error.message}
                    {error.stack && (
                      <>
                        <br />
                        <br />
                        <strong>Stack Trace:</strong>
                        <pre className="error-boundary__stack-trace">{error.stack}</pre>
                      </>
                    )}
                  </div>
                </details>
              )}

              {errorId && (
                <div className="error-boundary__error-id">
                  Error ID: {errorId}
                  {error.digest && ` • Digest: ${error.digest}`}
                </div>
              )}
            </div>
          </div>
        </div>
      </body>
    </html>
  );
}
